import React, { useState, useEffect } from 'react';
import AdminLayout from '../components/AdminLayout';
import { motion } from 'framer-motion';
import { FaPlus, FaEdit, FaTrash, FaEye, FaSearch } from 'react-icons/fa';
import { apiService } from '../services/api';
import api from '../services/api';
import { normalizeListForAdmin, toBool } from '../utils/normalizeApi';

const NewsManagement = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'General',
    publishedDate: new Date().toISOString().split('T')[0],
    isActive: true,
    priority: 1
  });

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      // Fetch ALL news (active + inactive) for admin panel
      const response = await api.get('/news/all');
      console.log('📰 Fetched news from API:', response.data);
      console.log('Total news count:', response.data.length);
      setNews(normalizeListForAdmin(response.data));
    } catch (error) {
      console.error('Error fetching news:', error);
      // Use mock data if API fails
      setNews([
        { id: 1, title: 'Admissions Open 2026-27', description: 'Apply now for new session', category: 'Admission', publishedDate: '2026-03-01', isActive: true, priority: 1 },
        { id: 2, title: 'Sports Day Celebration', description: 'Annual sports day on March 25', category: 'Events', publishedDate: '2026-03-10', isActive: true, priority: 2 }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await apiService.news.update(editingId, formData);
        alert('✅ News updated successfully!');
      } else {
        await apiService.news.create(formData);
        alert('✅ News created successfully!');
      }
      setShowForm(false);
      setEditingId(null);
      resetForm();
      fetchNews();
    } catch (error) {
      console.error('CRUD Error:', error);
      const errorMsg = error.response?.data?.error || error.message;
      const details = error.response?.data?.details;
      
      let finalMessage = errorMsg;
      if (details && Array.isArray(details)) {
        finalMessage += '\n\n' + details.map(d => `• ${d.path}: ${d.message}`).join('\n');
      }
      
      alert('❌ Error:\n' + finalMessage);
    }
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setFormData({
      title: item.title,
      description: item.description,
      category: item.category || 'General',
      publishedDate: new Date(item.publishedDate).toISOString().split('T')[0],
      isActive: toBool(item.isActive, true),
      priority: item.priority || 1
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this news?')) return;
    try {
      await apiService.news.delete(id);
      alert('✅ News deleted successfully!');
      fetchNews();
    } catch (error) {
      console.error('Delete error:', error);
      alert('❌ Error deleting news: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleToggleStatus = async (item) => {
    const newStatus = !toBool(item.isActive, true);
    try {
      await apiService.news.update(item.id, { ...item, isActive: newStatus });
      alert(`✅ News ${newStatus ? 'activated' : 'deactivated'}!`);
      fetchNews();
    } catch (error) {
      console.error('Toggle status error:', error);
      alert('❌ Error updating status: ' + (error.response?.data?.message || error.message));
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      category: 'General',
      publishedDate: new Date().toISOString().split('T')[0],
      isActive: true,
      priority: 1
    });
  };

  const filteredNews = news.filter(item =>
    item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-xl">Loading news...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex justify-between items-center mb-6"
        >
          <h1 className="text-3xl font-bold text-blue-900">News Management</h1>
          <button
            onClick={() => { setShowForm(true); setEditingId(null); resetForm(); }}
            className="bg-blue-900 text-white px-6 py-3 rounded-lg hover:bg-blue-800 transition flex items-center gap-2"
          >
            <FaPlus /> Add New News
          </button>
        </motion.div>

        {/* Search Bar */}
        <div className="mb-6 relative">
          <input
            type="text"
            placeholder="Search news..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-3 pl-12 border rounded-lg focus:ring-2 focus:ring-blue-900"
          />
          <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
        </div>

        {/* Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            >
              <h2 className="text-2xl font-bold text-blue-900 mb-6">
                {editingId ? 'Edit News' : 'Add New News'}
              </h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-900"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description *</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    required
                    rows="4"
                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-900"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-900"
                    >
                      <option value="General">General</option>
                      <option value="Admission">Admission</option>
                      <option value="Events">Events</option>
                      <option value="Achievement">Achievement</option>
                      <option value="Academic">Academic</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Published Date</label>
                    <input
                      type="date"
                      value={formData.publishedDate}
                      onChange={(e) => setFormData({ ...formData, publishedDate: e.target.value })}
                      className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-900"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
                    <input
                      type="number"
                      value={formData.priority}
                      onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) })}
                      min="1"
                      max="10"
                      className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-900"
                    />
                  </div>
                  <div className="flex items-center mt-6">
                    <input
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                      className="w-5 h-5 text-blue-900 rounded focus:ring-blue-900"
                    />
                    <label className="ml-2 text-sm text-gray-700">Active</label>
                  </div>
                </div>
                <div className="flex gap-4 pt-4">
                  <button
                    type="submit"
                    className="flex-1 bg-blue-900 text-white py-3 rounded-lg hover:bg-blue-800 transition font-semibold"
                  >
                    {editingId ? 'Update' : 'Create'} News
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowForm(false); setEditingId(null); resetForm(); }}
                    className="flex-1 bg-gray-300 text-gray-700 py-3 rounded-lg hover:bg-gray-400 transition font-semibold"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}

        {/* News Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-xl overflow-hidden"
        >
          <table className="w-full">
            <thead className="bg-blue-900 text-white">
              <tr>
                <th className="px-6 py-4 text-left">Title</th>
                <th className="px-6 py-4 text-left">Category</th>
                <th className="px-6 py-4 text-left">Date</th>
                <th className="px-6 py-4 text-left">Status</th>
                <th className="px-6 py-4 text-left">Priority</th>
                <th className="px-6 py-4 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredNews.map((item, index) => (
                <tr key={item.id} className={`border-b hover:bg-gray-50 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                  <td className="px-6 py-4">
                    <div>
                      <div className="font-semibold text-gray-800">{item.title}</div>
                      <div className="text-sm text-gray-500 truncate max-w-xs">{item.description}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="inline-block bg-blue-100 text-blue-900 px-3 py-1 rounded-full text-sm">
                      {item.category}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-gray-600">
                    {new Date(item.publishedDate).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <span className={`inline-block px-3 py-1 rounded-full text-sm ${
                        item.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {item.isActive ? 'Active' : 'Inactive'}
                      </span>
                      <button
                        onClick={() => handleToggleStatus(item)}
                        className={`text-xs px-2 py-1 rounded hover:bg-gray-200 transition ${
                          item.isActive ? 'text-red-600' : 'text-green-600'
                        }`}
                        title={item.isActive ? 'Deactivate' : 'Activate'}
                      >
                        {item.isActive ? '▼' : '▲'}
                      </button>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex">
                      {[...Array(5)].map((_, i) => (
                        <div key={i} className={`w-2 h-6 mx-0.5 rounded ${
                          i < Math.min(item.priority || 1, 5) ? 'bg-blue-900' : 'bg-gray-300'
                        }`} />
                      ))}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEdit(item)}
                        className="text-blue-600 hover:text-blue-800 p-2 hover:bg-blue-50 rounded"
                        title="Edit"
                      >
                        <FaEdit />
                      </button>
                      <button
                        onClick={() => handleDelete(item.id)}
                        className="text-red-600 hover:text-red-800 p-2 hover:bg-red-50 rounded"
                        title="Delete"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredNews.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              No news articles found. Click "Add New News" to create one.
            </div>
          )}
        </motion.div>
      </div>
    </AdminLayout>
  );
};

export default NewsManagement;
